import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setAuthData } from "../../features/authSlice";
import { persistor } from "../../redux/store";
import LoginImage from "../../assets/image/LoginImage.png";
import Logo from "../../assets/image/Logo.png";
import ShowPassword from "../../assets/svg/ShowPassword.svg";
import HidePassword from "../../assets/svg/HidePassword.svg";
import { API } from "../../shared/api/Api";
import GmailLogo from "../../assets/svg/GmailLogo.svg";
import Loader from "../../components/Live/Loader";
import { showToast } from "../../utils/utils";
import AnimatedBookLoader from "../../components/AnimatedBookLoader/AnimatedBookLoader";
import { setAuthCookies } from "../../utils/cookieUtils";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 120 },
  },
};

const Login = () => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const lesson = searchParams.get("lesson");
  const NEXT_LIVE_URL = process.env.REACT_APP_FRONTEND_STREAM_URL;

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const getLoginType = (value) => {
    if (/^\S+@\S+\.\S+$/.test(value)) return "email";
    if (/^\d{10}$/.test(value)) return "phone";
    if (/^[\w]+$/.test(value)) return "username";
    return "invalid";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const loginType = getLoginType(login);

    if (loginType === "invalid") {
      setLoading(false);
      showToast(
        "Validation Error",
        "Please enter a valid email, phone number, or username.",
        "error"
      );
      return;
    }

    let processedLogin = login;

    if (loginType === "phone") {
      processedLogin = login.replace(/^0/, "");
      processedLogin = `+963${processedLogin}`;
    } else if (loginType === "username") {
      processedLogin = `@${login}`;
    }

    try {
      const { headers, data } = await axios.post(
        API.Admin.Login,
        {
          login: processedLogin,
          password: password,
        },
        {
          headers: {
            lang: "en",
            Accept: "application/json",
          },
        }
      );

      if (data.status === 200 && data.errorCode === 0) {
        setLoading(false);
        if (data.data.twoFactorAuth && !data.data.token) {
          nav("/Verification", {
            state: {
              type: 1,
              uuid: data.data.uuid,
              redirect: redirect === 'live' ? NEXT_LIVE_URL : (location?.state?.redirect || null)
            },
          });
        } else {
          // Set auth data in Redux
          dispatch(setAuthData(data));

          // Set auth cookies
          try {
            setAuthCookies(data);
          } catch (cookieError) {
            console.error('Failed to store in cookies:', cookieError);
          }

          // Wait for Redux persist to complete before navigating
          await persistor.flush();

          showToast("Welcome", data.message, "success");

          // Small delay to ensure state is fully persisted and available
          setTimeout(() => {
            if (redirect == 'live') {
              // Redirect to the redirect-to-live page with lesson parameter
              const lessonParam = lesson ? `?lesson=${lesson}` : '';
              window.location.href = `${NEXT_LIVE_URL}/redirect-to-live${lessonParam}`;
            } else if (location?.state?.redirect) {
              nav(location.state.redirect);
            } else {
              nav("/dashboard/home");
            }
          }, 100); // Small delay to ensure state is available
        }
      } else {
        setLoading(false);
        showToast("Something went wrong", data.message, "error");
      }
    } catch (err) {
      setLoading(false);
      showToast("An error occurred. Please try again.", "Error", "error");
    }
  };

  const handleGmailLogin = async () => {
    window.location.href = `${API.Admin.redirect}/google`;
  };

  useEffect(() => {
    const autofilledLogin = document.querySelector("#login")?.value;
    const autofilledPassword = document.querySelector("#password")?.value;

    if (autofilledLogin) setLogin(autofilledLogin);
    if (autofilledPassword) setPassword(autofilledPassword);
  }, []);

  if (loading) {
    return (
      <div className="bg-[#EBEBEB] min-h-screen flex justify-center items-center h-screen">
        <AnimatedBookLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-accent lg:flex lg:items-center">
      {/* Login Form Section */}
      <div className="w-full bg-white lg:w-[40%] lg:min-h-screen px-6 p-0 sl:px-14 ll:px-7 h-screen overflow-y-auto">
        <motion.div
          className="mx-auto h-screen flex flex-col justify-center max-w-[493px] space-y-2 lg:space-y-8 ll:space-y-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Logo Section */}
          <motion.div
            className="space-y-2 sl:space-y-6 ll:space-y-8 text-center"
            variants={itemVariants}
          >
            <motion.img
              className="mx-auto h-18 w-18 sm:h-16 sm:w-16 sl:h-24 sl:w-24 ll:h-32 ll:w-32 transition-transform duration-300 hover:scale-105"
              src={Logo}
              alt="Company logo"
              whileHover={{ rotate: -2, scale: 1.05 }}
            />
            <div className="space-y-2 sl:space-y-3">
              <h1 className="font-anek-odia text-lg sl:text-2xl ll:text-3xl font-bold text-primary">
                Login to your account
              </h1>
              <p className="text-secondary text-sm lg:text-md ll:text-lg">
                Language Village, Where Every Language Finds a Home
              </p>
            </div>
          </motion.div>

          {/* Login Form */}
          <motion.form
            className="space-y-6 sl:space-y-3"
            onSubmit={handleSubmit}
            variants={itemVariants}
          >
            <div className="space-y-4 sl:space-y-3">
              <motion.input
                type="text"
                id="login"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="Email, Username or Phone"
                className="w-full h-input md:h-input-md sl:h-input-md ll:h-input-lg px-4 md:px-6 sl:px-6 ll:px-8 bg-accent rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base md:text-md sl:text-md ll:text-lg placeholder:text-secondary/70"
                whileHover={{ scale: 1.01 }}
                variants={itemVariants}
              />
              <motion.div className="relative" variants={itemVariants}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full h-input md:h-input-md sl:h-input-md ll:h-input-lg px-4 md:px-6 sl:px-6 ll:px-8 bg-accent rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base md:text-md sl:text-md ll:text-lg placeholder:text-secondary/70"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-lg p-2 hover:bg-accent"
                >
                  <motion.img
                    className="h-6 w-6 sl:h-6 sl:w-6"
                    src={showPassword ? HidePassword : ShowPassword}
                    alt="Password toggle"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  />
                </button>
              </motion.div>
            </div>

            <motion.button
              type="submit"
              className="w-full h-input md:h-input-md sl:h-input-md ll:h-input-lg bg-primary text-white text-md md:text-md sl:text-md ll:text-lg rounded-lg hover:bg-primary-hover transition-colors font-medium"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
            >
              Login
            </motion.button>
          </motion.form>

          {/* Additional Actions */}
          <motion.div
            className="space-y-6 sl:space-y-2"
            variants={containerVariants}
          >
            <motion.div className="text-center" variants={itemVariants}>
              <a
                href="/password/forget"
                className="text-sm sm:text-base text-secondary hover:text-primary transition-colors duration-300"
              >
                Forgot your password?
              </a>
            </motion.div>

            <motion.div className="flex items-center" variants={itemVariants}>
              <div className="flex-1 border-t-2 border-accent-dark" />
              <span className="px-4 text-sm sm:text-base text-secondary">
                or continue with
              </span>
              <div className="flex-1 border-t-2 border-accent-dark" />
            </motion.div>

            <motion.div
              className="space-y-4 sl:space-y-3 ll:space-y-4"
              variants={containerVariants}
            >
              <motion.button
                type="button"
                onClick={handleGmailLogin}
                className="flex w-full items-center justify-center gap-4 h-input md:h-input-md sl:h-input-md ll:h-input-lg text-white rounded-xl border-2 border-accent-dark bg-white py-3.5 sm:py-4 transition-all duration-300 hover:border-primary hover:bg-accent"
                whileHover={{ y: -2 }}
                variants={itemVariants}
              >
                <img
                  src={GmailLogo}
                  className="h-6 w-6 sm:h-7 sm:w-7"
                  alt="Google logo"
                />
                <span className="font-medium text-secondary text-md sl:text-md ll:text-lg">
                  Continue with Google
                </span>
              </motion.button>

              <motion.button
                type="button"
                onClick={() => nav("/Register", { state: { redirect: location?.state?.redirect } })}
                className="w-full rounded-xl h-input md:h-input-md sl:h-input-md ll:h-input-lg bg-primary text-white text-md md:text-md sl:text-md ll:text-lg border-2 border-primary font-medium transition-all duration-300 hover:bg-primary hover:text-white"
                whileHover={{ y: -2 }}
                variants={itemVariants}
              >
                Create Account
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Image Section */}
      <motion.div
        className="hidden lg:flex lg:w-[60%] h-screen relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <motion.img
          className="h-full w-full object-cover"
          src={LoginImage}
          alt="Decorative login visual"
          initial={{ x: 100 }}
          animate={{ x: 0 }}
          transition={{ type: "spring", stiffness: 50 }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/30 via-transparent to-transparent" />
        <div className="absolute bottom-4 w-full text-center text-white text-sm sm:text-base px-4">
          <p className="mb-2">
            By creating this account, you agree to our{" "}
            <a href="#" className="border-b border-white">
              terms of service
            </a>
          </p>
          <p>Language Village 2024</p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
